import React, { useMemo, useRef, useCallback, useEffect } from "react";
import { UseFormReturn, FormProvider } from "react-hook-form";
import { ConfigFormItem } from "./ConfigFormRenderer";
import { ToolParameterCard } from "./ToolParameterCard";
import { ActionToolConfig, ConfigResponse } from "@/generated";
import { NoxusFormEntryConfig } from "../Spotflow/ConfigDrawer/ConfigDrawer";
import { Separator } from "../shadcn/separator";
import { Badge } from "../shadcn/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../shadcn/accordion";
import { FileUploadField } from "./FileInputField";
import { RichTextField } from "./RichTextField";
import { FileTypes } from "@/utils/mimetypes";

interface InputConnector {
  name: string;
  label: string;
  type: string;
  optional: boolean;
  toolMode: "llm" | "user" | "hidden";
}

interface ToolFormRendererProps {
  config?: Record<string, NoxusFormEntryConfig>;
  inputs?: ConfigResponse["inputs"];
  form: UseFormReturn<any, any, any>;
  toolConfig: ActionToolConfig;
  onToolConfigChange: (config: ActionToolConfig) => void;
}

const transformFieldConfig = (
  fieldConfig: NoxusFormEntryConfig
): NoxusFormEntryConfig => {
  const newConfig = { ...fieldConfig } as any;

  // Remove description
  delete newConfig.description;

  // Update display property
  if (newConfig.display) {
    const newDisplay = { ...newConfig.display };
    delete newDisplay.label;

    newConfig.display = newDisplay;
  }

  return newConfig;
};

export const ToolFormRenderer = ({
  config,
  inputs,
  form,
  toolConfig,
  onToolConfigChange,
}: ToolFormRendererProps) => {
  const visibleConfigFields = useMemo(() => {
    if (!config) return {};
    return Object.fromEntries(
      Object.entries(config).filter(([_, fieldConfig]) => {
        const cfg = fieldConfig as any;
        // Include fields with tool_mode="llm" or tool_mode="user"
        return cfg?.tool_mode === "llm" || cfg?.tool_mode === "user";
      })
    );
  }, [config]);

  // Fields with tool_mode="user" cannot be exposed to LLM - they're always fixed
  const userModeFields = useMemo(() => {
    if (!config) return new Set<string>();
    return new Set(
      Object.entries(config)
        .filter(([_, fieldConfig]) => {
          const cfg = fieldConfig as any;
          return cfg?.tool_mode === "user";
        })
        .map(([fieldName]) => fieldName)
    );
  }, [config]);

  const coreConfigFields = useMemo(() => {
    return Object.fromEntries(
      Object.entries(visibleConfigFields).filter(([_, fieldConfig]) => {
        const cfg = fieldConfig as any;
        return !cfg?.advanced && !cfg?.accordion;
      })
    );
  }, [visibleConfigFields]);

  const advancedConfigFields = useMemo(() => {
    return Object.fromEntries(
      Object.entries(visibleConfigFields).filter(([_, fieldConfig]) => {
        const cfg = fieldConfig as any;
        return cfg?.advanced === true || !!cfg?.accordion;
      })
    );
  }, [visibleConfigFields]);

  const inputConnectors = useMemo<InputConnector[]>(() => {
    if (!inputs) return [];
    return inputs
      .filter((input) => {
        const toolMode = (input as any).tool_mode || "hidden";
        return toolMode === "llm" || toolMode === "user";
      })
      .map((input) => ({
        name: input.name,
        label: input.label || input.name,
        type: input.definition?.data_type || "string",
        optional: input.optional || false,
        toolMode: (input as any).tool_mode || "hidden",
      }));
  }, [inputs]);

  const exposedConfigs = useMemo(() => {
    if (Array.isArray(toolConfig.exposed_configs)) {
      // Filter out user-mode fields - they can never be exposed to LLM
      return new Set(
        toolConfig.exposed_configs.filter((name) => !userModeFields.has(name))
      );
    }
    // By default, expose all llm-mode fields (excluding user-mode)
    return new Set(
      Object.keys(visibleConfigFields).filter(
        (name) => !userModeFields.has(name)
      )
    );
  }, [toolConfig.exposed_configs, visibleConfigFields, userModeFields]);

  // Input connectors with tool_mode="user" cannot be exposed to LLM
  const userModeInputs = useMemo(() => {
    return new Set(
      inputConnectors
        .filter((input) => input.toolMode === "user")
        .map((input) => input.name)
    );
  }, [inputConnectors]);

  const exposedInputs = useMemo(() => {
    if (Array.isArray(toolConfig.exposed_inputs)) {
      // Filter out user-mode inputs - they can never be exposed to LLM
      return new Set(
        toolConfig.exposed_inputs.filter((name) => !userModeInputs.has(name))
      );
    }
    // By default, expose all llm-mode inputs (excluding user-mode)
    return new Set(
      inputConnectors.filter((i) => i.toolMode === "llm").map((i) => i.name)
    );
  }, [toolConfig.exposed_inputs, inputConnectors, userModeInputs]);

  const handleToggleConfigExposed = (fieldName: string) => {
    // User-mode fields cannot be toggled - they're always fixed
    if (userModeFields.has(fieldName)) {
      return;
    }

    const newExposedConfigs = new Set(exposedConfigs);
    if (newExposedConfigs.has(fieldName)) {
      newExposedConfigs.delete(fieldName);
    } else {
      newExposedConfigs.add(fieldName);
      form.setValue(fieldName, undefined);
    }

    const formValues = form.getValues();
    const newConfigOverrides: Record<string, unknown> = {};
    Object.keys(visibleConfigFields).forEach((fn) => {
      // Include both non-exposed and user-mode fields in overrides
      const isFixed = !newExposedConfigs.has(fn) || userModeFields.has(fn);
      if (isFixed && formValues[fn] !== undefined) {
        newConfigOverrides[fn] = formValues[fn];
      }
    });

    const newInputOverrides: Record<string, unknown> = {};
    inputConnectors.forEach((input) => {
      // Include both non-exposed and user-mode inputs in overrides
      const isFixed =
        !exposedInputs.has(input.name) || userModeInputs.has(input.name);
      if (isFixed && formValues[input.name] !== undefined) {
        newInputOverrides[input.name] = formValues[input.name];
      }
    });

    onToolConfigChange({
      config_overrides: newConfigOverrides,
      input_overrides: newInputOverrides,
      exposed_configs: Array.from(newExposedConfigs),
      exposed_inputs: Array.from(exposedInputs),
    });
  };

  const handleToggleInputExposed = (inputName: string) => {
    // User-mode inputs cannot be toggled - they're always fixed
    if (userModeInputs.has(inputName)) {
      return;
    }

    const newExposedInputs = new Set(exposedInputs);
    if (newExposedInputs.has(inputName)) {
      newExposedInputs.delete(inputName);
    } else {
      newExposedInputs.add(inputName);
      form.setValue(inputName, undefined);
    }

    const formValues = form.getValues();
    const newConfigOverrides: Record<string, unknown> = {};
    Object.keys(visibleConfigFields).forEach((fn) => {
      if (!exposedConfigs.has(fn) && formValues[fn] !== undefined) {
        newConfigOverrides[fn] = formValues[fn];
      }
    });

    const newInputOverrides: Record<string, unknown> = {};
    inputConnectors.forEach((input) => {
      // Include both non-exposed and user-mode inputs in overrides
      const isFixed =
        !newExposedInputs.has(input.name) || userModeInputs.has(input.name);
      if (isFixed && formValues[input.name] !== undefined) {
        newInputOverrides[input.name] = formValues[input.name];
      }
    });

    onToolConfigChange({
      config_overrides: newConfigOverrides,
      input_overrides: newInputOverrides,
      exposed_configs: Array.from(exposedConfigs),
      exposed_inputs: Array.from(newExposedInputs),
    });
  };

  const handleFormChange = useCallback(() => {
    const formValues = form.getValues();
    const newConfigOverrides: Record<string, unknown> = {};
    Object.keys(visibleConfigFields).forEach((fn) => {
      // Include both non-exposed and user-mode fields in overrides
      const isFixed = !exposedConfigs.has(fn) || userModeFields.has(fn);
      if (isFixed && formValues[fn] !== undefined) {
        newConfigOverrides[fn] = formValues[fn];
      }
    });

    const newInputOverrides: Record<string, unknown> = {};
    inputConnectors.forEach((input) => {
      // Include both non-exposed and user-mode inputs in overrides
      const isFixed =
        !exposedInputs.has(input.name) || userModeInputs.has(input.name);
      if (isFixed && formValues[input.name] !== undefined) {
        newInputOverrides[input.name] = formValues[input.name];
      }
    });

    onToolConfigChange({
      config_overrides: newConfigOverrides,
      input_overrides: newInputOverrides,
      exposed_configs: Array.from(exposedConfigs),
      exposed_inputs: Array.from(exposedInputs),
    });
  }, [
    form,
    visibleConfigFields,
    exposedConfigs,
    userModeFields,
    inputConnectors,
    exposedInputs,
    userModeInputs,
    onToolConfigChange,
  ]);

  useEffect(() => {
    const subscription = form.watch(() => {
      handleFormChange();
    });
    return () => subscription.unsubscribe();
  }, [form, handleFormChange]);

  const hasCoreConfigFields = Object.keys(coreConfigFields).length > 0;
  const hasAdvancedConfigFields = Object.keys(advancedConfigFields).length > 0;
  const hasInputs = inputConnectors.length > 0;

  if (!hasCoreConfigFields && !hasAdvancedConfigFields && !hasInputs) {
    return (
      <div className="text-sm text-muted-foreground">
        This action has no configurable parameters.
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <div className="space-y-4" onBlur={handleFormChange}>
        {hasCoreConfigFields && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Core parameters</p>
            <div className="border border-border rounded-lg overflow-hidden">
              {Object.entries(coreConfigFields).map(
                ([fieldName, fieldConfig], index) => {
                  const isUserMode = userModeFields.has(fieldName);
                  const isExposed =
                    !isUserMode && exposedConfigs.has(fieldName);
                  const configItem = fieldConfig as any;
                  const isLast =
                    index === Object.entries(coreConfigFields).length - 1;

                  return (
                    <div key={fieldName}>
                      <ToolParameterCard
                        label={configItem?.display?.label || fieldName}
                        description={configItem?.description}
                        isExposed={isExposed}
                        onToggle={() => handleToggleConfigExposed(fieldName)}
                        showModeToggle={!isUserMode}
                      >
                        <ConfigFormItem
                          field_key={fieldName}
                          config={transformFieldConfig(fieldConfig)}
                          fieldPrefix=""
                          disabled={false}
                        />
                      </ToolParameterCard>
                      {!isLast && <Separator />}
                    </div>
                  );
                }
              )}
            </div>
          </div>
        )}

        {hasInputs && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Input Connectors</p>
            <div className="border border-border rounded-lg overflow-hidden">
              {inputConnectors.map((input, index) => {
                const isExposed = exposedInputs.has(input.name);
                const isLast = index === inputConnectors.length - 1;

                const renderInputField = () => {
                  if (
                    input.type === "Image" ||
                    input.type === "Audio" ||
                    input.type === "File"
                  ) {
                    return (
                      <FileUploadField
                        control={form.control}
                        name={input.name}
                        canSelectMultipleFiles={false}
                        variant="logo"
                        allowed_file_types={
                          input.type === "Image"
                            ? [FileTypes.IMAGE]
                            : input.type === "Audio"
                              ? [FileTypes.AUDIO]
                              : undefined
                        }
                        isDisabled={false}
                        rules={{ required: !input.optional }}
                      />
                    );
                  }
                  return (
                    <RichTextField
                      control={form.control}
                      name={input.name}
                      isControlled={true}
                      isDisabled={false}
                    />
                  );
                };

                return (
                  <div key={input.name}>
                    <ToolParameterCard
                      label={input.label}
                      type={input.type}
                      optional={input.optional}
                      isExposed={isExposed}
                      onToggle={() => handleToggleInputExposed(input.name)}
                      showModeToggle={input.toolMode === "llm"}
                    >
                      {renderInputField()}
                    </ToolParameterCard>
                    {!isLast && <Separator />}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {hasAdvancedConfigFields && (
          <Accordion type="multiple" className="w-full">
            <AccordionItem value="advanced" className="border-none">
              <AccordionTrigger className="text-sm font-medium py-4 hover:no-underline">
                <div className="flex items-center gap-2">
                  <span>Advanced parameters</span>
                  <Badge variant="outline" className="text-xs">
                    {Object.keys(advancedConfigFields).length}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="border border-border rounded-lg overflow-hidden">
                  {Object.entries(advancedConfigFields).map(
                    ([fieldName, fieldConfig], index) => {
                      const isUserMode = userModeFields.has(fieldName);
                      const isExposed =
                        !isUserMode && exposedConfigs.has(fieldName);
                      const configItem = fieldConfig as any;
                      const isLast =
                        index ===
                        Object.entries(advancedConfigFields).length - 1;

                      return (
                        <div key={fieldName}>
                          <ToolParameterCard
                            label={configItem?.display?.label || fieldName}
                            description={configItem?.description}
                            isExposed={isExposed}
                            onToggle={() =>
                              handleToggleConfigExposed(fieldName)
                            }
                            showModeToggle={!isUserMode}
                          >
                            <ConfigFormItem
                              field_key={fieldName}
                              config={transformFieldConfig(fieldConfig)}
                              fieldPrefix=""
                              disabled={false}
                            />
                          </ToolParameterCard>
                          {!isLast && <Separator />}
                        </div>
                      );
                    }
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </div>
    </FormProvider>
  );
};
